import { Button } from 'antd';
import './VtxButton.scss';
import { ButtonProps } from 'antd/lib/button';
import React, { forwardRef, ReactNode } from 'react';
import classNames from 'classnames';
import { assertNever } from '../../helpers/neverCheckers';
import { IAutomatable, preventDataAutomationOverriding } from '../../helpers/IAutomatable';
import { isIconProps, VtxIcon, VtxIconProps } from '../icon/VtxIcon';

export type VtxButtonType = 'primary' | 'primary-deprecated' | 'default' | 'link' | 'danger';

export interface VtxButtonProps
    extends Omit<ButtonProps, 'type' | 'shape' | 'icon' | 'forwardRef'>,
        IAutomatable {
    /**
     * This changes the color and styles of the button
     */
    type?: VtxButtonType;
    /**
     * @deprecated the shape of the button is determined by the style guide maintainers and should not be modified
     */
    shape?: never;
    /**
     * @deprecated
     */
    ghost?: never;
    /**
     * To specify the direction of where icon is displayed
     * @default 'left'
     */
    iconDirection?: 'right' | 'left';
    icon?: ReactNode | Omit<VtxIconProps, 'fontSize'>;
}

type VtxBtnClasses =
    | 'vtx-btn-primary'
    | 'vtx-btn-primary-deprecated'
    | 'vtx-btn-default'
    | 'vtx-btn-link'
    | 'vtx-btn-danger';

interface IBtnTypeMapping {
    antType: ButtonProps['type'];
    isDanger?: boolean;
    vtxBtnClass: VtxBtnClasses;
}

const ourStylesToAntD = (input: VtxButtonProps['type']): IBtnTypeMapping => {
    if (!input || input === 'default') {
        return { antType: 'default', vtxBtnClass: 'vtx-btn-default' };
    } else if (input === 'primary') {
        return { antType: 'primary', vtxBtnClass: 'vtx-btn-primary' };
    } else if (input === 'primary-deprecated') {
        return {
            antType: 'primary',
            vtxBtnClass: 'vtx-btn-primary-deprecated',
        };
    } else if (input === 'danger') {
        return { antType: 'default', vtxBtnClass: 'vtx-btn-danger', isDanger: true };
    } else if (input === 'link') {
        return { antType: 'link', vtxBtnClass: 'vtx-btn-link' };
    } else {
        return assertNever(input);
    }
};

export const vtxButtonPropsToAntDProps = (props: VtxButtonProps): ButtonProps => {
    const automationClass = 'vtx-button';
    const { className, type, iconDirection, icon, disabled, ...otherProps } = preventDataAutomationOverriding(
        props
    );
    const typeMapped = ourStylesToAntD(type);
    const listOfClassNames = [automationClass, typeMapped.vtxBtnClass, className];

    const classNamesCombined =
        iconDirection === 'right'
            ? classNames(listOfClassNames, 'vtx-right-direction-icon')
            : classNames(listOfClassNames);
    const allProps: ButtonProps = {
        ...otherProps,
        ...{
            className: classNamesCombined,
            'data-automation-class': automationClass,
            type: typeMapped.antType,
            danger: typeMapped.isDanger,
            icon: icon && isIconProps(icon) ? <VtxIcon {...icon} /> : icon,
            disabled: disabled,
        },
    };
    return allProps;
};

export const VtxButton = forwardRef((props: VtxButtonProps, ref: React.Ref<HTMLButtonElement>) => {
    const { ...otherProps } = vtxButtonPropsToAntDProps(preventDataAutomationOverriding(props));
    return (
        <Button ref={ref} {...otherProps}>
            {props.children}
        </Button>
    );
});
